import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Package, ShoppingBag } from "lucide-react";
import { api, inr } from "../lib/api";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const STATUS_COLORS = {
  pending: "bg-[#C5A059]/15 text-[#9c7a2e]",
  confirmed: "bg-[#1F4D2E]/10 text-[#1F4D2E]",
  shipped: "bg-blue-100 text-blue-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const Account = () => {
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (customer === false) navigate("/login");
    else if (customer) api.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [customer, navigate]);

  if (customer === null) return <div className="py-24 text-center text-[#8A9A8D]">Loading...</div>;
  if (!customer) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif font-bold text-3xl text-[#1C2B20]">My Account</h1>
          <p className="text-[#5A6B5D] mt-1">Hello, {customer.name} · {customer.email}</p>
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          data-testid="account-logout-btn"
          className="inline-flex items-center gap-2 border border-[#1F4D2E] text-[#1F4D2E] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1F4D2E] hover:text-white transition-colors"
        >
          <LogOut size={16} /> Logout
        </button>
      </div>

      <h2 className="font-serif text-xl font-bold text-[#1C2B20] mb-4">My Orders</h2>
      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E2DAD0] p-10 text-center">
          <ShoppingBag size={44} className="mx-auto text-[#C5A059]" />
          <p className="text-[#5A6B5D] mt-4">You have no orders yet.</p>
          <Link to="/shop" className="mt-5 inline-block bg-[#1F4D2E] hover:bg-[#163A22] text-white font-medium rounded-full px-6 py-2.5 transition-colors">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-[#E2DAD0] p-5" data-testid={`account-order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-sm font-semibold text-[#1C2B20]">#{o.id.slice(0, 8).toUpperCase()}</p>
                  <p className="text-xs text-[#8A9A8D]">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full capitalize ${STATUS_COLORS[o.status]}`} data-testid={`account-order-status-${o.id}`}>
                  {o.status}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-[#E2DAD0] pt-3">
                {o.items.map((it, idx) => (
                  <span key={idx} className="text-xs bg-[#F5F0E6] rounded-full px-3 py-1 text-[#5A6B5D] flex items-center gap-1">
                    <Package size={12} /> {it.name} ×{it.quantity}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex gap-6 text-sm text-[#5A6B5D]">
                <span>Total: <b className="text-[#1C2B20]">{inr(o.subtotal)}</b></span>
                <span>Advance paid: <b className="text-[#1F4D2E]">{inr(o.advance_amount)}</b></span>
                <span>Balance (COD): <b className="text-[#1C2B20]">{inr(o.balance_due)}</b></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Account;
